import React from 'react';
import { useTranslation } from 'react-i18next';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import CustomEditor from 'ckeditor5-custom-build';
import 'ckeditor5-custom-build/build/translations/fr';
import 'ckeditor5-custom-build/build/translations/de';
import 'ckeditor5-custom-build/build/translations/it';

import './CKWysiwyg.css';

const toHtmlString = (value) => (typeof value === 'string' ? value : '');

const normalize_empty_html = (html) => {
    const value = toHtmlString(html).trim();
    if (!value) {
        return '';
    }
    if (/^<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>$/i.test(value)) {
        return '';
    }
    return value;
};

const CKWysiwyg = ({
    defaultValue, onChange, withShortcodes, withColors, withImages, withTables, readOnly,
    textColor, linksColor,
}) => {
    const { i18n } = useTranslation();
    const editor_ref = React.useRef(null);
    const last_emitted_data = React.useRef(toHtmlString(defaultValue));
    const is_internal_change = React.useRef(false);

    const content_style = React.useMemo(() => {
        const style = {};
        if (textColor)
            style['--ck-email-text-color'] = textColor;
        if (linksColor)
            style['--ck-email-links-color'] = linksColor;
        return Object.keys(style).length ? style : undefined;
    }, [textColor, linksColor]);

    React.useEffect(() => {
        const next = toHtmlString(defaultValue);
        if (normalize_empty_html(next) === normalize_empty_html(last_emitted_data.current)) {
            return;
        }
        if (is_internal_change.current) {
            is_internal_change.current = false;
            last_emitted_data.current = next;
            return;
        }
        last_emitted_data.current = next;
        const editor = editor_ref.current;
        if (editor && normalize_empty_html(editor.getData()) !== normalize_empty_html(next)) {
            editor.setData(next);
        }
    }, [defaultValue]);

    const items = React.useMemo(() => {
        const items = withShortcodes ? ['shortcodes', '|'] : [];
        items.push('heading');
        items.push('|');
        items.push('bold');
        items.push('italic');
        items.push('underline');
        items.push('link');

        if (withColors) {
            items.push('fontColor');
            items.push('fontBackgroundColor');
        }

        items.push('alignment');
        items.push('|');
        items.push('horizontalLine');
        items.push('|');
        items.push('bulletedList');
        items.push('numberedList');
        items.push('outdent');
        items.push('indent');

        if (withImages || withTables)
            items.push('|');

        if ( withTables)
            items.push('insertTable');

        if (withImages)
            items.push('imageUpload');

        items.push('|');
        items.push('strikethrough');
        items.push('superscript');
        items.push('blockQuote');
        items.push('|');
        items.push('sourceEditing');
        items.push('removeFormat');
        items.push('|');
        items.push('undo');
        items.push('redo');

        return items;
    }, [withShortcodes, withColors, withImages, withTables]);

    const editor_config = React.useMemo(() => ({
        licenseKey: process.env.VITE_CKEDITOR_LICENSE_KEY || 'GPL',
        language: i18n.language,
        toolbar: {
            items: readOnly ? [] : items,
            // Narrow sidebars (email layout editor): wrap instead of grouping behind
            // "…" — the overflow panel was clipped by overflow:hidden parents.
            shouldNotGroupWhenFull: true,
        },
    }), [i18n.language, items, readOnly]);

    return (
        <div
            className={[
                'CKWysiwyg',
                textColor ? 'has-text-color' : '',
                linksColor ? 'has-links-color' : '',
            ].filter(Boolean).join(' ')}
            style={content_style}
        >
            <CKEditor
                editor={ CustomEditor }
                config={editor_config}
                disabled={readOnly}
                onReady={ editor => {
                    editor_ref.current = editor;
                    const initial = toHtmlString(defaultValue);
                    if (initial) {
                        editor.setData(initial);
                    }
                    last_emitted_data.current = initial;
                    if (readOnly) {
                        editor.enableReadOnlyMode('ticketack-read-only');
                    }
                }}
                onChange={(event, editor) => {
                    if (readOnly || !onChange || editor.state === 'destroyed') {
                        return;
                    }
                    const data = editor.getData();
                    if (normalize_empty_html(data) === normalize_empty_html(last_emitted_data.current)) {
                        return;
                    }
                    last_emitted_data.current = data;
                    is_internal_change.current = true;
                    onChange(data);
                }}
                onBlur={(_event, _editor) => {}}
                onFocus={(_event, _editor) => {}}
            />
        </div>
    );
}

export default CKWysiwyg;
